const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export async function getPrograms({ search = '' } = {}) {
  // Para evitar inconsistencias (como registros ya eliminados que siguen apareciendo),
  // usamos siempre el endpoint completo estándar, sin pasar por /select/.
  const headers = getAuthHeaders();
  const baseFullUrl = `${API_BASE}/escolar/programas-academicos/`;

  const params = new URLSearchParams();
  const trimmedSearch = String(search || '').trim();
  if (trimmedSearch) {
    params.append('search', trimmedSearch);
  }

  const queryString = params.toString();
  const fullUrl = queryString ? `${baseFullUrl}?${queryString}` : baseFullUrl;

  const res = await fetch(fullUrl, { headers, cache: 'no-store' });
  if (!res.ok) throw new Error(`GET /programas/ ${res.status}`);

  const data = await res.json();
  // Transformar al shape que consume la tabla (ProgramsTable)
  return Array.isArray(data) ? data.map(mapApiToRow) : [];
}

// Transforma el objeto del formulario del modal a payload del backend
export function mapFormToPayload(form) {
  const toInt = (v) => {
    const n = parseInt(String(v || '').trim(), 10);
    return Number.isFinite(n) ? n : null;
  };
  let id_tipo_periodo = form.periodTypeId;
  // Solo enviar PK numérico válido, nunca string
  if (id_tipo_periodo && !isNaN(Number(id_tipo_periodo))) id_tipo_periodo = Number(id_tipo_periodo);
  else id_tipo_periodo = undefined;
  const payload = {
    codigo_programa: form.code && String(form.code).trim() ? String(form.code).trim() : undefined,
    numero_revoe: form.rvoe || '',
    nombre_programa: form.name || '',
    id_institucion: form.institution ? Number(form.institution) : 1,
    id_seccion: form.section ? Number(form.section) : null,
    id_tipo_periodo: id_tipo_periodo,
    campus_temp: form.campus || '',
    duracion_periodos: toInt(form.duration),
    creditos_totales: toInt(form.credits),
    descripcion: form.description || '',
    objetivos: form.objectives || '',
    perfil_egresado: form.profile || '',
    fecha_registro_revoe: form.rvoeDate || null,
    ciclo_inicio: form.cycleId || '',
    modalidad: form.modalidad || null,
    activo: form.status === 'ACTIVO',
  };
  // Eliminar undefined para evitar enviar campos vacíos innecesarios
  Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
  return payload;
}

export async function createProgram(form) {
  const payload = mapFormToPayload(form);
  const res = await fetch(`${API_BASE}/escolar/programas-academicos/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST /programas/ ${res.status}: ${text}`);
  }
  // El endpoint de creación no retorna el objeto serializado para la tabla;
  // solicitamos nuevamente la lista para obtener el mapping estándar.
  const updated = await getPrograms();
  // Notificar al resto de la app que la lista cambió
  try { window.dispatchEvent(new CustomEvent('programs:updated', { detail: updated })); } catch (_) {}
  return updated;
}

export async function getProgram(id) {
  const res = await fetch(`${API_BASE}/escolar/programas-academicos/${id}/`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error(`GET /programas/${id}/ ${res.status}`);
  return res.json();
}

export async function updateProgram(id, form) {
  if (!id) throw new Error('ID de programa no proporcionado');
  const raw = mapFormToPayload(form);
  // Limpiar campos vacíos para no sobreescribir con strings vacíos
  const payload = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== '' && v !== null && v !== undefined));
  // Usar PATCH para actualización parcial y evitar resets involuntarios
  const res = await fetch(`${API_BASE}/escolar/programas-academicos/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`PUT /programas/${id}/ ${res.status}: ${text}`);
  }
  const updated = await getPrograms();
  try { window.dispatchEvent(new CustomEvent('programs:updated', { detail: updated })); } catch (_) {}
  return updated;
}

export async function deleteProgram(id) {
  const res = await fetch(`${API_BASE}/escolar/programas-academicos/${id}/`, { 
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (res.status !== 204 && !res.ok) {
    const text = await res.text();
    throw new Error(`DELETE /programas/${id}/ ${res.status}: ${text}`);
  }
  const updated = await getPrograms();
  try { window.dispatchEvent(new CustomEvent('programs:updated', { detail: updated })); } catch (_) {}
  return updated;
}

// Helpers
function mapApiToRow(api) {
  // Backend ProgramaSerializer devuelve campos del modelo EscProgramaAcademico
  const formatPeriod = (p) => {
    if (!p) return '—';
    const m = String(p).toLowerCase();
    if (m === 'tetramestre' || m === 'tetramestral') return 'Tetramestre';
    if (m === 'semestre' || m === 'semestral') return 'Semestre';
    return p;
  };
  
  // Extraer nombres de relaciones (pueden venir como objeto o como string)
  const getInstitutionName = () => {
    if (api.institution_name) return api.institution_name;
    if (api.id_institucion && typeof api.id_institucion === 'object') return api.id_institucion.nombre_institucion;
    return '—';
  };
  
  const getSectionName = () => {
    if (api.section_name) return api.section_name;
    if (api.id_seccion && typeof api.id_seccion === 'object') return api.id_seccion.nombre_seccion;
    return '—';
  };
  
  const getPeriodTypeName = () => {
    if (api.tipo_periodo_name) return api.tipo_periodo_name;
    if (api.tipo_periodo) return api.tipo_periodo;
    if (api.id_tipo_periodo && typeof api.id_tipo_periodo === 'object') return api.id_tipo_periodo.nombre_periodo;
    return '';
  };
  
  // Determinar estado
  const status = api.activo === false ? 'INACTIVO' : 'ACTIVO';
  
  return {
    id: api.id_programa || api.id,
    name: api.nombre_programa || api.name || '',
    institutionName: getInstitutionName(),
    campus_id: api.campus_id || null,
    campus: api.campus_name || '—',
    campus_ids: Array.isArray(api.campus_ids) ? api.campus_ids.filter((v) => v != null) : (api.campus_id ? [api.campus_id] : []),
    section: getSectionName(),
    code: api.codigo_programa || api.code || '',
    period: formatPeriod(getPeriodTypeName()),
    duration: api.duracion_periodos ?? api.duration ?? null,
    credits: String(api.creditos_totales ?? api.credits ?? ''),
    status: status,
    isActive: api.activo !== false,
    sectionId: (() => {
      if (!api.id_seccion) return null;
      if (typeof api.id_seccion === 'number') return api.id_seccion;
      if (typeof api.id_seccion === 'object') return api.id_seccion.id_seccion || api.id_seccion.id || null;
      return Number(api.id_seccion) || null;
    })(),
    // Campos adicionales para edición
    rvoe: api.numero_revoe || '',
    description: api.descripcion || '',
    objectives: api.objetivos || '',
    profile: api.perfil_egresado || '',
    rvoeDate: api.fecha_registro_revoe || '',
    cycleId: api.ciclo_inicio || '',
    modalidad: api.modalidad || '',
  };
}
